<template>
  <div class="bs">
    <!-- 顶栏 -->
    <div class="bs-bar">
      <span class="bs-info">关卡 {{ level }} / {{ MAX_LEVEL }}</span>
      <span class="bs-info">👆 {{ moves }} 步</span>
      <span class="bs-info">🏆 最高 {{ unlockedLevel }} 关</span>
      <button
          type="button"
          class="bs-btn bs-btn-hint"
          :disabled="hints <= 0 || status !== 'playing'"
          title="AI 推荐一步（使用后本关不能撤销）"
          @click="useHint">
        💡 提示 ×{{ hints }}
      </button>
      <button
          type="button"
          class="bs-btn"
          :disabled="undoStack.length === 0 || hinted || status !== 'playing'"
          title="撤销上一步"
          @click="undo">
        ↩️ 撤销
      </button>
      <button type="button" class="bs-btn" :title="soundOn ? '关闭音效' : '开启音效'" @click="soundOn = !soundOn">
        {{ soundOn ? '🔊' : '🔇' }}
      </button>
      <button type="button" class="bs-btn" title="重开本关" @click="loadLevel(level)">🔄</button>
    </div>

    <!-- 选关 -->
    <div class="bs-levels" :class="{ open: levelOpen }">
      <button
          v-for="lv in MAX_LEVEL"
          :key="lv"
          type="button"
          class="bs-level"
          :class="{ active: lv === level, locked: lv > unlockedLevel }"
          @click="pickLevel(lv)">
        {{ lv }}
      </button>
    </div>
    <button type="button" class="bs-menu-toggle" @click="levelOpen = !levelOpen">
      {{ levelOpen ? '收起选关 ▲' : '选择关卡 ▼' }}
    </button>

    <!-- 试管区 -->
    <div class="bs-board">
      <div
          v-for="(tube, i) in tubes"
          :key="i"
          class="bs-tube"
          :class="{
            selected: selected === i,
            done: isDoneTube(tube),
            'hint-from': hintFrom === i,
            'hint-to': hintTo === i
          }"
          @click="tubeClick(i)">
        <div class="bs-tube-body">
          <div
              v-for="(ball, b) in tube"
              :key="'b' + b"
              class="bs-ball"
              :style="{ bottom: b * ballStep + 'px', background: ballColor(ball) }"></div>
        </div>
      </div>
    </div>

    <p class="bs-tip">点击选中一个试管，再点击另一个试管倒球：只有顶球同色或空管才能倒入哦～</p>

    <!-- 过关遮罩 -->
    <div v-if="status === 'won'" class="bs-overlay">
      <div class="bs-overlay-emoji">🎉</div>
      <p class="bs-overlay-title">过关啦！</p>
      <p class="bs-overlay-info">第 {{ level }} 关 · 用了 {{ moves }} 步完成</p>
      <div class="bs-overlay-btns">
        <button v-if="level < MAX_LEVEL" type="button" class="bs-btn-pink" @click="loadLevel(level + 1)">下一关</button>
        <button type="button" class="bs-btn-line" @click="loadLevel(level)">重玩本关</button>
      </div>
    </div>
  </div>
</template>

<script>
/* =====================================================
 * 粉星小球分类（复刻 y7x7 Ball Sort）
 * 30 关程序生成（单球打乱 + 可解性保证），2 个空管
 * 选择/倒球 / 撤销 / AI 提示 / 音效 / 进度存档
 * ===================================================== */
const MAX_LEVEL = 30
const LV_KEY = 'pink_ballsort_level'

const COLORS = [
  '#ff6fa5', '#ffb04d', '#9b6dff', '#4fc6a4',
  '#4da3f0', '#f26ab6', '#ff7d6b', '#ffd98a',
  '#35a8b5', '#d05f8a', '#7ec8ff', '#c9a6ff'
]

// 每关配置：色数 / 容量（管数 = 色数 + 2 个空管）
function levelConfig (lv) {
  if (lv <= 3) return { colors: 2, cap: 4 }
  if (lv <= 7) return { colors: 3, cap: 4 }
  if (lv <= 12) return { colors: 4, cap: 4 }
  if (lv <= 18) return { colors: 5, cap: 4 }
  if (lv <= 23) return { colors: 6, cap: 5 }
  if (lv <= 27) return { colors: 7, cap: 5 }
  return { colors: 8, cap: 5 }
}

// 可复现的伪随机数（每关布局固定，可重玩同一关）
function mulberry32 (seed) {
  let a = seed >>> 0
  return function () {
    a |= 0
    a = (a + 0x6D2B79F5) | 0
    let t = Math.imul(a ^ (a >>> 15), 1 | a)
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

export default {
  name: 'BallSort',
  data () {
    return {
      MAX_LEVEL,
      level: 1,
      unlockedLevel: 1,
      tubes: [],
      cap: 4,
      selected: -1,
      moves: 0,
      undoStack: [],
      hinted: false,
      hints: 2,
      status: 'playing', // playing | won
      levelOpen: false,
      soundOn: true,
      hintFrom: -1,
      hintTo: -1,
      audioCtx: null
    }
  },
  computed: {
    ballStep () {
      // 球直径 + 间距，随容量自适应
      return Math.min(30, Math.floor(220 / this.cap))
    }
  },
  created () {
    try {
      this.unlockedLevel = Math.min(MAX_LEVEL, parseInt(localStorage.getItem(LV_KEY), 10) || 1)
    } catch (e) {
      this.unlockedLevel = 1
    }
    this.loadLevel(1)
  },
  beforeDestroy () {
    if (this.audioCtx) {
      try { this.audioCtx.close() } catch (e) {}
    }
  },
  methods: {
    /* ---------- 关卡 ---------- */
    loadLevel (lv) {
      if (lv < 1 || lv > MAX_LEVEL) return
      this.level = lv
      this.selected = -1
      this.moves = 0
      this.undoStack = []
      this.hinted = false
      this.hints = 2
      this.status = 'playing'
      this.hintFrom = -1
      this.hintTo = -1
      this.generate(lv)
    },
    pickLevel (lv) {
      if (lv > this.unlockedLevel) return
      this.levelOpen = false
      this.loadLevel(lv)
    },
    generate (lv) {
      const cfg = levelConfig(lv)
      const colors = cfg.colors
      const cap = cfg.cap
      this.cap = cap
      const tubes = colors + 2
      const rnd = mulberry32(1000 + lv * 777)
      // 从目标态开始做随机单球打乱（可解性由构造保证）
      for (let attempt = 0; attempt < 20; attempt++) {
        const tubesArr = []
        for (let c = 0; c < colors; c++) {
          tubesArr.push(Array(cap).fill(c + 1))
        }
        for (let e = 0; e < 2; e++) tubesArr.push([])
        const steps = colors * cap * (5 + Math.min(7, Math.floor(lv / 3)))
        for (let s = 0; s < steps; s++) {
          const srcs = []
          for (let i = 0; i < tubes; i++) if (tubesArr[i].length) srcs.push(i)
          const src = srcs[Math.floor(rnd() * srcs.length)]
          const dsts = []
          for (let i = 0; i < tubes; i++) {
            if (i !== src && tubesArr[i].length < cap) dsts.push(i)
          }
          if (!dsts.length) continue
          const dst = dsts[Math.floor(rnd() * dsts.length)]
          tubesArr[dst].push(tubesArr[src].pop())
        }
        // 打乱后不能是“全统一”的平凡态（保证有可玩性）
        const mixed = tubesArr.filter(t => t.length && new Set(t).size > 1).length
        if (mixed >= 2) {
          this.tubes = tubesArr
          return
        }
      }
      // 极端兜底：再补几次乱序
      const tubesArr = []
      for (let c = 0; c < colors; c++) {
        tubesArr.push(Array(cap).fill(c + 1))
      }
      for (let e = 0; e < 2; e++) tubesArr.push([])
      const flat = []
      for (let c = 0; c < colors; c++) {
        for (let k = 0; k < cap; k++) flat.push(c + 1)
      }
      for (let i = flat.length - 1; i > 0; i--) {
        const j = Math.floor(rnd() * (i + 1))
        ;[flat[i], flat[j]] = [flat[j], flat[i]]
      }
      let idx = 0
      for (let i = 0; i < tubes; i++) {
        const n = i < colors ? cap : 0
        tubesArr[i] = flat.slice(idx, idx + n)
        idx += n
      }
      this.tubes = tubesArr
    },

    /* ---------- 渲染辅助 ---------- */
    ballColor (v) {
      return COLORS[(v - 1) % COLORS.length]
    },
    isDoneTube (tube) {
      return tube.length > 0 && tube.length === this.cap && new Set(tube).size === 1
    },

    /* ---------- 玩法 ---------- */
    tubeClick (i) {
      if (this.status !== 'playing') return
      if (this.selected === -1) {
        if (this.tubes[i].length) {
          this.selected = i
        }
        return
      }
      if (this.selected === i) {
        this.selected = -1
        return
      }
      const from = this.selected
      this.selected = -1
      if (this.tryPour(from, i)) {
        this.moves++
        this.checkWin()
      }
    },
    // 倒球：顶球同色或空管、且有空间
    tryPour (from, to) {
      const a = this.tubes[from]
      const b = this.tubes[to]
      if (!a.length) return false
      const col = a[a.length - 1]
      if (b.length && b[b.length - 1] !== col) return false
      const space = this.cap - b.length
      if (space <= 0) return false
      // 计算顶部同色连续球数
      let k = 0
      for (let i = a.length - 1; i >= 0; i--) {
        if (a[i] !== col) break
        k++
      }
      const moveCount = Math.min(k, space)
      const moved = a.splice(a.length - moveCount, moveCount)
      for (let i = 0; i < moved.length; i++) b.push(moved[i])
      this.undoStack.push({ from, to, count: moveCount })
      if (this.undoStack.length > 60) this.undoStack.shift()
      this.playTone(moveCount > 1 ? 480 : 380, 0.08)
      return true
    },
    undo () {
      if (this.status !== 'playing' || this.hinted) return
      const last = this.undoStack.pop()
      if (!last) return
      const b = this.tubes[last.to]
      const a = this.tubes[last.from]
      const moved = b.splice(b.length - last.count, last.count)
      for (let i = 0; i < moved.length; i++) a.push(moved[i])
      this.playTone(260, 0.07)
    },
    checkWin () {
      const done = this.tubes.every(t => t.length === 0 || new Set(t).size === 1)
      if (done) {
        this.status = 'won'
        this.playWin()
        if (this.level >= this.unlockedLevel && this.level < MAX_LEVEL) {
          this.unlockedLevel = Math.min(MAX_LEVEL, this.level + 1)
          try { localStorage.setItem(LV_KEY, String(this.unlockedLevel)) } catch (e) {}
        }
      }
    },

    /* ---------- AI 提示 ---------- */
    useHint () {
      if (this.hints <= 0 || this.status !== 'playing') return
      const best = this.findBestMove()
      if (!best) return
      this.hints--
      this.hinted = true
      this.hintFrom = best.from
      this.hintTo = best.to
      this.tryPour(best.from, best.to)
      this.moves++
      this.playTone(560, 0.1)
      const self = this
      setTimeout(() => {
        self.hintFrom = -1
        self.hintTo = -1
      }, 700)
      this.checkWin()
    },
    // 给合法倒球打分：清空试管 > 合并同色 > 其它；并避免无意义往返
    findBestMove () {
      const n = this.tubes.length
      let best = null
      let bestScore = -Infinity
      const last = this.undoStack[this.undoStack.length - 1]
      for (let from = 0; from < n; from++) {
        const a = this.tubes[from]
        if (!a.length) continue
        const col = a[a.length - 1]
        let k = 0
        for (let i = a.length - 1; i >= 0; i--) {
          if (a[i] !== col) break
          k++
        }
        for (let to = 0; to < n; to++) {
          if (from === to) continue
          const b = this.tubes[to]
          if (b.length && b[b.length - 1] !== col) continue
          if (b.length + k > this.cap) continue
          // 正在倒的球是“完成管”的顶球时跳过（避免拆完成管）
          if (b.length === this.cap && new Set(b).size === 1) continue
          // 无意义往返：刚撤走又倒回
          if (last && last.from === to && last.to === from) continue
          let score = 1
          if (a.length - k === 0) score += 100 // 清空源管
          if (b.length + k === this.cap) score += 60 // 填满目标管
          if (b.length && b[b.length - 1] === col) score += 30 // 同色合并
          if (a.length === this.cap && new Set(a).size === 1) score -= 80 // 拆完成管
          if (score > bestScore) {
            bestScore = score
            best = { from, to }
          }
        }
      }
      return best
    },

    /* ---------- 音效 ---------- */
    playTone (freq, dur) {
      if (!this.soundOn) return
      try {
        if (!this.audioCtx) {
          this.audioCtx = new (window.AudioContext || window.webkitAudioContext)()
        }
        const ctx = this.audioCtx
        const osc = ctx.createOscillator()
        const gain = ctx.createGain()
        osc.type = 'sine'
        osc.frequency.value = freq
        gain.gain.setValueAtTime(0.12, ctx.currentTime)
        gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + dur)
        osc.connect(gain)
        gain.connect(ctx.destination)
        osc.start()
        osc.stop(ctx.currentTime + dur + 0.02)
      } catch (e) {}
    },
    playWin () {
      if (!this.soundOn) return
      const self = this
      ;[523, 659, 784].forEach((f, i) => {
        setTimeout(() => self.playTone(f, 0.18), i * 140)
      })
    }
  }
}
</script>

<style scoped>
.bs {
  font-family: "PingFang SC", "Hiragino Sans GB", "Microsoft YaHei", sans-serif;
  color: #6b4a5a;
  max-width: 760px;
  margin: 0 auto;
}

/* ---------- 顶栏 ---------- */
.bs-bar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  padding: 10px 16px;
  margin-bottom: 12px;
  border-radius: 14px;
  background: #fff7fb;
  border: 1px solid #ffddea;
}
.bs-info {
  font-size: 14px;
  font-weight: 600;
  color: #b06a8a;
}
.bs-btn {
  border: 2px solid #ffd3e4;
  border-radius: 999px;
  padding: 7px 14px;
  background: #fff;
  color: #e25b96;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s ease;
}
.bs-btn:hover:not(:disabled) { border-color: #ff6fa5; background: #ffeef6; }
.bs-btn:disabled { opacity: 0.45; cursor: not-allowed; }
.bs-btn-hint { margin-left: auto; }

/* ---------- 选关 ---------- */
.bs-levels {
  display: none;
  grid-template-columns: repeat(10, 1fr);
  gap: 8px;
  padding: 12px;
  margin-bottom: 10px;
  border-radius: 14px;
  background: #fff7fb;
  border: 1px solid #ffddea;
}
.bs-levels.open { display: grid; }
.bs-level {
  aspect-ratio: 1;
  border: 2px solid #ffd3e4;
  border-radius: 10px;
  background: #fff;
  color: #e25b96;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s ease;
}
.bs-level:hover:not(.locked) { border-color: #ff6fa5; background: #ffeef6; }
.bs-level.active {
  background: linear-gradient(135deg, #ff8fc7, #ff6fa5);
  border-color: #ff6fa5;
  color: #fff;
}
.bs-level.locked { opacity: 0.4; cursor: not-allowed; color: #c9b3c0; }
.bs-menu-toggle {
  display: block;
  margin: 0 auto 14px;
  border: none;
  background: transparent;
  color: #c490a8;
  font-size: 13px;
  cursor: pointer;
}
.bs-menu-toggle:hover { color: #e25b96; }

/* ---------- 试管区 ---------- */
.bs-board {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 14px;
  padding: 18px;
  border-radius: 20px;
  background: linear-gradient(160deg, #fff0f7, #ffe9f3);
  border: 1px solid #ffddea;
}
.bs-tube {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  transition: transform 0.18s ease, filter 0.18s ease;
}
.bs-tube:hover { filter: brightness(1.04); }
.bs-tube.selected { transform: translateY(-10px); }
.bs-tube-body {
  position: relative;
  width: 44px;
  height: 224px;
  border: 3px solid #e8a8c8;
  border-top: none;
  border-radius: 4px 4px 16px 16px;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.55), rgba(255, 214, 235, 0.5));
  box-shadow: inset 0 6px 14px rgba(255, 158, 198, 0.25);
  overflow: hidden;
}
.bs-ball {
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  width: 34px;
  height: 30px;
  border-radius: 50%;
  transition: bottom 0.22s ease;
  box-shadow: inset 0 -5px 8px rgba(0, 0, 0, 0.18), inset 0 4px 6px rgba(255, 255, 255, 0.5), 0 2px 4px rgba(120, 60, 90, 0.25);
}
.bs-tube.done .bs-tube-body {
  border-color: #ff8fc7;
  box-shadow: 0 0 0 3px rgba(255, 143, 199, 0.35), inset 0 6px 14px rgba(255, 158, 198, 0.25);
}
.bs-tube.hint-from .bs-tube-body {
  animation: bs-glow 0.7s ease infinite;
}
.bs-tube.hint-to .bs-tube-body {
  animation: bs-glow 0.7s ease infinite 0.15s;
}
@keyframes bs-glow {
  0%, 100% { box-shadow: 0 0 0 3px #ff3d8f, inset 0 6px 14px rgba(255, 158, 198, 0.25); }
  50% { box-shadow: 0 0 0 6px rgba(255, 61, 143, 0.45), inset 0 6px 14px rgba(255, 158, 198, 0.25); }
}
.bs-tip {
  text-align: center;
  font-size: 12px;
  color: #c490a8;
  margin-top: 12px;
}

/* ---------- 过关遮罩 ---------- */
.bs-overlay {
  position: fixed;
  inset: 0;
  z-index: 60;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  background: rgba(255, 240, 247, 0.85);
  backdrop-filter: blur(3px);
}
.bs-overlay-emoji { font-size: 56px; animation: bs-bounce 0.5s ease; }
@keyframes bs-bounce {
  0% { transform: scale(0.4); opacity: 0; }
  60% { transform: scale(1.15); }
  100% { transform: scale(1); opacity: 1; }
}
.bs-overlay-title {
  font-size: 24px;
  font-weight: 800;
  color: #e25b96;
}
.bs-overlay-info { font-size: 14px; color: #b06a8a; }
.bs-overlay-btns {
  display: flex;
  gap: 12px;
  margin-top: 8px;
}
.bs-btn-pink, .bs-btn-line {
  border: none;
  border-radius: 999px;
  padding: 10px 26px;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s ease;
}
.bs-btn-pink {
  background: linear-gradient(135deg, #ff8fc7, #ff6fa5);
  color: #fff;
  box-shadow: 0 5px 14px rgba(255, 111, 165, 0.35);
}
.bs-btn-pink:hover { transform: translateY(-2px); }
.bs-btn-line {
  background: #fff;
  border: 2px solid #ffd3e4;
  color: #b06a8a;
}
.bs-btn-line:hover { border-color: #ff6fa5; color: #e25b96; }

@media (max-width: 700px) {
  .bs-levels { grid-template-columns: repeat(6, 1fr); }
  .bs-tube-body { height: 190px; width: 38px; }
  .bs-ball { width: 29px; height: 25px; }
}
</style>
